import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight, BadgeCheck } from 'lucide-react';
import { useLang } from '../i18n';
import { useSEO } from '../seo';
import { PageHeader } from '../components/PageHeader';
import { Reveal } from '../components/Reveal';
import { PublicPartnerProducts } from '../components/PublicPartnerProducts';
import { PartnerCard } from '../components/PartnerCard';
import { AccommodationCard } from '../components/AccommodationCard';
import { Stay22Block } from '../components/stay22/Stay22Block';
import { useCatalog } from '../data/catalog';
import { useCuratedCopy } from '../data/curatedArchitecture';
import { EcoAssistContextButton } from '../components/EcoAssistContextButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ISLANDS = ['São Tomé', 'Príncipe', 'Santiago'];
const PROPERTY_TYPES = ['hotel', 'eco-lodge', 'boutique-hotel', 'resort', 'guest-house', 'local-accommodation', 'apartment', 'villa'];

export default function AccommodationsPage({ stayDiscover = false }) {
  const { t, lang } = useLang();
  const curated = useCuratedCopy(lang);
  const [params] = useSearchParams();
  const destinationParam = (params.get('destination') || '').trim();
  useSEO('Alojamentos | Eco Collection', 'Hotéis, lodges e estadias locais selecionadas em São Tomé e Príncipe e Cabo Verde.');
  const [partners, setPartners] = useState([]);
  const [accommodations, setAccommodations] = useState([]);
  const [island, setIsland] = useState(() => (ISLANDS.includes(destinationParam) ? destinationParam : 'Todos'));
  const [propertyType, setPropertyType] = useState('');

  useEffect(() => {
    fetch(`${API}/partners?type=accommodation`)
      .then((r) => r.json())
      .then((d) => setPartners(Array.isArray(d) ? d : []))
      .catch(() => setPartners([]));
  }, []);

  useEffect(() => {
    const query = new URLSearchParams();
    if (island !== 'Todos') query.set('island', island);
    if (propertyType) query.set('property_type', propertyType);
    const q = query.toString() ? `?${query}` : '';
    fetch(`${API}/accommodations${q}`)
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setAccommodations(Array.isArray(d) ? d : []))
      .catch(() => setAccommodations([]));
  }, [island, propertyType]);

  const visible = useMemo(() => partners.filter((p) => island === 'Todos' || p.island === island), [partners, island]);
  const { destinations } = useCatalog();
  const [destSettings, setDestSettings] = useState([]);
  useEffect(() => {
    fetch(`${API}/destinations`)
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setDestSettings(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);
  const destMatch = useMemo(
    () => destinations.find((d) => destinationParam && (d.name.toLowerCase().includes(destinationParam.toLowerCase()) || destinationParam.toLowerCase().includes(d.name.split(' ')[0].toLowerCase()))),
    [destinations, destinationParam]
  );
  const destEmbedId = destSettings.find((d) => d.slug === destMatch?.slug)?.stay22_embed_id || '';

  return (
    <div data-testid="accommodations-page">
      <PageHeader kicker={stayDiscover ? curated.stayKicker : t('page.accommodations.k')} title={stayDiscover ? curated.stayTitle : t('page.accommodations.t')} sub={stayDiscover ? curated.stayText : t('page.accommodations.s')} />

      <section className="bg-eco-cream py-14 md:py-20">
        <div className="mx-auto max-w-6xl px-5 md:px-8">
          <Reveal>
            <div className="flex items-center gap-2.5">
              <BadgeCheck size={18} className="shrink-0 text-eco-green" />
              <h2 className="text-xl leading-[1.3] font-semibold tracking-[-0.015em] text-eco-ink" data-testid="accommodations-local-title">{t('acc.local.t')}</h2>
            </div>
            <p className="eco-body mt-2 text-eco-ink/60" data-testid="accommodations-local-sub">{t('acc.local.s')}</p>
            {stayDiscover && <EcoAssistContextButton context="stay" className="mt-5">{curated.assist.stay}</EcoAssistContextButton>}
          </Reveal>

          <Reveal className="mt-4 max-w-xs">
            <label><span className="eco-field-label mb-1.5 block">{t('stay.filter.type')}</span><select value={propertyType} onChange={(event) => setPropertyType(event.target.value)} className="eco-input"><option value="">{t('filter.all')}</option>{PROPERTY_TYPES.map((type) => <option key={type} value={type}>{t(`stay.type.${type}`)}</option>)}</select></label>
          </Reveal>

          <Reveal className="mt-6 flex flex-wrap gap-2">
            {['Todos', ...ISLANDS].map((i) => (
              <button
                key={i}
                data-testid={`accommodations-filter-${i.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={() => setIsland(i)}
                className={`rounded-full px-5 py-2 text-sm font-medium transition-colors duration-300 ${
                  island === i ? 'bg-eco-green text-white' : 'bg-white text-eco-ink/70 hover:bg-eco-green/10'
                }`}
              >
                {i === 'Todos' ? t('filter.all') : i}
              </button>
            ))}
          </Reveal>

          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="accommodations-grid">
            {accommodations.map((a, i) => (
              <Reveal key={a.id} delay={(i % 3) * 0.08}>
                <AccommodationCard acc={a} />
              </Reveal>
            ))}
            {accommodations.length === 0 && (
              <div data-testid="accommodations-grid-empty" className="col-span-full rounded-3xl border border-dashed border-eco-ink/15 bg-white py-14 text-center">
                <p className="text-sm text-eco-ink/50">{t('acc.empty')}</p>
                <Link to={`/plan-my-trip?destination=${encodeURIComponent(island === 'Todos' ? '' : island)}`} data-testid="accommodations-grid-plan-cta" className="eco-btn-text mt-3 inline-flex justify-center">{t('nav.plan')} <ArrowRight size={14} /></Link>
              </div>
            )}
          </div>

          {visible.length > 0 && (
            <>
              <Reveal className="mt-14">
                <h3 className="text-base font-semibold tracking-[-0.01em] text-eco-ink" data-testid="accommodations-network-title">{t('acc.network.t')}</h3>
              </Reveal>
              <div className="mt-5 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {visible.map((p, i) => (
                  <Reveal key={p.id} delay={(i % 3) * 0.08}>
                    <PartnerCard
                      partner={p}
                      typeLabel={t('type.accommodation')}
                      testid={`accommodation-card-${p.id}`}
                      showPrice
                      cta={t('cta.checkstay')}
                    />
                  </Reveal>
                ))}
              </div>
            </>
          )}
          <PublicPartnerProducts type="accommodation" island={island} />
        </div>
      </section>

      <section className="bg-white py-14 md:py-20" data-testid="stay22-section">
        <div className="mx-auto max-w-3xl px-5 md:px-8">
          <Reveal>
            <Stay22Block
              destination={destinationParam || (island !== 'Todos' ? island : '')}
              lat={destMatch?.coords?.[0] ?? null}
              lng={destMatch?.coords?.[1] ?? null}
              campaign="eco_accommodations"
              embedId={destEmbedId}
              showMap={Boolean(destEmbedId) || (island === 'Todos' ? !destinationParam : island === 'São Tomé')}
            />
          </Reveal>
        </div>
      </section>
    </div>
  );
}
